import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingUp, TrendingDown, Target, DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Typogaphy } from 'vnetwork-ui'
import { SCHEMES } from '@/components/EchartCustomized'
import { SalesTargetFilters } from '@/modules/Report/components/BusinessTargets/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getEmployeeDashboard } from '@/modules/Report/services'
import { formatVND } from '@/modules/Report/components/TargetAnalytics/helpers'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useAuthStore from '@/stores/useAuthStore'

type DeltaType = 'increase' | 'decrease' | 'neutral'

interface StatCardProps {
    title: string
    value: string | number
    icon?: React.ReactNode
    iconColor?: string
    delta?: string
    deltaType?: DeltaType
    deltaDescription?: string
    className?: string
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    icon,
    delta,
    deltaType = 'neutral',
    deltaDescription,
    className,
}) => (
    <div
        className={cn(
            'bg-white rounded-md border border-gray-100 p-3 flex items-center gap-3',
            className
        )}
    >
        {icon && <div className="shrink-0">{icon}</div>}
        <div className="flex flex-col gap-0.5 min-w-0">
            <Typogaphy className="text-xs text-gray-500 truncate">
                {title}
            </Typogaphy>
            <Typogaphy className="text-sm font-semibold text-gray-900">
                {value}
            </Typogaphy>
            {delta && (
                <div className="flex items-center gap-1">
                    {deltaType === 'increase' && (
                        <TrendingUp className="w-3 h-3 text-green-600" />
                    )}
                    {deltaType === 'decrease' && (
                        <TrendingDown className="w-3 h-3 text-red-500" />
                    )}
                    <span
                        className={cn(
                            'text-[10px] font-medium',
                            deltaType === 'increase' && 'text-green-600',
                            deltaType === 'decrease' && 'text-red-500',
                            deltaType === 'neutral' && 'text-gray-400'
                        )}
                    >
                        {delta}
                    </span>
                    {deltaDescription && (
                        <span className="text-[10px] text-gray-400">
                            {deltaDescription}
                        </span>
                    )}
                </div>
            )}
        </div>
    </div>
)

interface StatCardsProps {
    filters: SalesTargetFilters
    className?: string
}

export default function StatCards({ className, filters }: StatCardsProps) {
    const { t } = useTranslation('report')
    const { isAdmin } = usePermissionsStore()
    const { employee } = useAuthStore()

    const { data: dashboardData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEE_DASHBOARD, filters],
        queryFn: () =>
            getEmployeeDashboard(
                filters,
                !isAdmin ? employee?.employee_id : undefined
            ),
        enabled:
            !!filters.employee_id &&
            filters.employee_id.length > 0 &&
            !!filters.gte &&
            !!filters.lte,
    })

    const stats = useMemo(() => {
        if (!dashboardData?.employees || dashboardData.employees.length === 0) {
            return { kpiRate: 0, averageMonthlyRevenue: 0 }
        }
        const employees = dashboardData.employees
        const achieversCount = employees.filter(
            (emp) => emp.achievement_percentage >= 100
        ).length
        const kpiRate = (achieversCount / employees.length) * 100
        const totalActualRevenue = employees.reduce(
            (sum, emp) => sum + emp.actual_revenue,
            0
        )

        let monthsCount = 1
        if (filters.gte && filters.lte) {
            const s = new Date(filters.gte)
            const e = new Date(filters.lte)
            monthsCount =
                (e.getFullYear() - s.getFullYear()) * 12 +
                (e.getMonth() - s.getMonth()) +
                1
            if (monthsCount < 1) monthsCount = 1
        }

        return {
            kpiRate,
            averageMonthlyRevenue: totalActualRevenue / monthsCount,
        }
    }, [dashboardData, filters.gte, filters.lte])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className={cn('grid grid-cols-2 gap-2', className)}>
            <StatCard
                title={t('target_analytics.stat_cards.kpi_rate')}
                value={`${stats.kpiRate.toFixed(1)}%`}
                icon={
                    <Target
                        className="w-4 h-4"
                        style={{ color: SCHEMES.warning[0] }}
                    />
                }
            />
            <StatCard
                title={t('target_analytics.stat_cards.avg_monthly_revenue')}
                value={formatVND(Math.round(stats.averageMonthlyRevenue))}
                icon={
                    <DollarSign
                        className="w-4 h-4"
                        style={{ color: SCHEMES.success[0] }}
                    />
                }
            />
        </div>
    )
}

export { StatCard }
export type { StatCardProps, DeltaType }
